import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import { useAuth } from '../../../../commons/Auth';
import { useAlert } from 'react-alert';
import {
  BtnOutlineGreen,
  ContFilterYear,
  ContPaginationPage,
} from '../../../../styled-components';
import SearchInput from '../../../../commons/SearchInput';
import generateMonthOptions from '../../../../lib/helpers/generateMonthOptions';
import generatePaginationOptions from '../../../../lib/helpers/generatePaginationOptions';
import TotalSignalsCondoRevenuesRow from './TotalSignalsCondoRevenuesRow';
import SignalsCondoRoyaltyTable from './SignalsCondoRoyaltyTable';
import moment from 'moment';
import ReportingModal from '../../../../commons/ReportingModal';
import { useLoading } from '../../../../commons/Loading';
import { createBonus } from '../../../../lib/fetch/reports';

const SignalsCondo = ({
  data,
  onFilter,
  total,
  query,
  profile,
  onAddBonus,
  fetchReportsSignals,
  profileId,
}) => {
  const [t] = useTranslation('SIGNALS');
  const [{ newIsAdmin, newToken }] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const { categories = {} } = data;
  const { variableRevenuesSignals = {} } = categories;
  const [amount, setAmount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const { data: dataRevSignals = [] } = variableRevenuesSignals;

  const origin = window.location.origin;
  let hash = window.location.hash.split('/');
  hash.pop();
  const prevPath =
    origin +
    '/' +
    hash.join('/') +
    `${query.year ? '?year=' + query.year : ''}`;

  const getVariableRevenuesSignalsTotals = (dataRev) => {
    const estimateTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.estimate || 0,
      0
    );
    const extraShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.extraShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableAdminShare,
      0
    );
    const iafsTotals = dataRev.reduce((prev, cur) => {
      const { iaf = {} } = cur;
      const iafAmount = iaf._id ? cur.adminShare : 0;
      return prev + iafAmount;
    }, 0);

    return {
      estimateTotals,
      extraShareTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
      iafsTotals,
    };
  };

  const toggle = ({ selectAll: toggleSelectAll = false } = {}) => {
    if (isOpen) {
      setSelectAll(false);
      return setIsOpen(false);
    }

    setSelectAll(toggleSelectAll);

    const validRows = dataRevSignals.map((row, index) => {
      if (row.checked || toggleSelectAll) {
        return {
          ...row,
          checked: !row.iaf && row.billableAdminShare > 0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    if (checkedRows.length) {
      const billableTotal = checkedRows.reduce(
        (acc, { billableAdminShare = 0 }) => acc + billableAdminShare,
        0
      );
      const alreadyBilled = checkedRows.reduce((acc, row) => {
        return (
          acc +
            row?.iaf?.reduce((acc, { amount = 0 }) => {
              return acc + amount || 0;
            }, 0) || 0
        );
      }, 0);
      setAmount(+(billableTotal - alreadyBilled).toFixed(2));
      setIsOpen(!isOpen);
    } else {
      const message = toggleSelectAll
        ? t('ROYALTY:NO_ROW_IAF_SELECTABLE')
        : t('ROYALTY:SELECT_AT_LEAST_ONE');
      alert.error(message);
    }
  };

  const onSubmit = async (formData) => {
    showLoading();
    const validRows = dataRevSignals.map((row, index) => {
      if (row.checked || selectAll) {
        return {
          ...row,
          checked: !row.iaf && row.billableAdminShare > 0,
        };
      }
      return row;
    });

    const checkedRows = validRows.filter(({ checked }) => checked);

    const accountabilityYear = moment(
      checkedRows[0]?.accountabilityDate
    ).year();
    for (const row of checkedRows) {
      const rowYear = moment(row.accountabilityDate).year();
      if (rowYear !== accountabilityYear) {
        alert.error(
          'Le segnalazioni selezionate presentano anni di rendicontazione differenti.'
        );
        toggle();
        hideLoading();
        return;
      }
    }

    const signalsByRole = checkedRows.reduce((acc, row) => {
      const role = row.matchedRole || 'signalman';
      if (!acc[role]) {
        acc[role] = [];
      }
      acc[role].push(row);
      return acc;
    }, {});

    try {
      for (const [role, signals] of Object.entries(signalsByRole)) {
        const roleAmount = signals.reduce(
          (acc, { billableAdminShare = 0 }) => acc + billableAdminShare,
          0
        );

        const data = {
          ...formData,
          accountability: accountabilityYear,
          iafType: 'SIGNALS',
          role,
          signals: signals.map(
            ({
              _id,
              accountabilityDate,
              adminInfo,
              adminShare,
              calculateAdminPerc,
              commercial,
              commercial2,
              commercial3,
              createdAt,
              estimate,
              extraPercentage,
              extraShare,
              reportingType,
              shareToInvoice,
              signalTypeInfo,
              signalman,
              signalmanShare,
              signalmanPosition,
              signalmanSharePerc,
              notes,
              matchedRole,
            }) => ({
              _id,
              accountabilityDate,
              admin: {
                _id: adminInfo?._id,
                businessName: adminInfo?.businessName,
                fiscalCode: adminInfo?.fiscalCode,
              },
              adminShare,
              calculateAdminPerc,
              commercial,
              commercial2,
              commercial3,
              createdAt,
              estimate,
              extraPercentage,
              extraShare,
              reportingType,
              shareToInvoice,
              signalTypeInfo,
              signalman: {
                _id: signalman?._id,
                businessName: signalman?.businessName,
                fiscalCode: signalman?.fiscalCode,
              },
              signalmanShare,
              signalmanPosition,
              signalmanSharePerc,
              notes,
              matchedRole,
            })
          ),
          amount: roleAmount,
        };
        const { error } = await createBonus({
          newToken,
          bonus: { ...data, profileId },
        });

        if (error) {
          alert.error(t('COMMON:GENERIC_ERROR'));
          hideLoading();
          return;
        }
      }
    } catch (error) {
      toggle();
    } finally {
      fetchReportsSignals();
      toggle();
      hideLoading();
      alert.success(t('COMMON:SUCCESS_CREATE'));
    }
  };

  return (
    <>
      <section style={{ marginTop: '1rem' }}>
        <Row>
          <Col xs="12" className="text-right">
            {newIsAdmin && (
              <BtnOutlineGreen
                type="button"
                onClick={toggle}
                style={{ marginRight: '16px' }}
              >
                {t('ROYALTY:INVITE_TO_INVOICE')}
              </BtnOutlineGreen>
            )}
            {newIsAdmin && (
              <BtnOutlineGreen
                type="button"
                onClick={() => toggle({ selectAll: true })}
                style={{ marginRight: '16px' }}
              >
                {t('ROYALTY:INVITE_TO_INVOICE_ALL')}
              </BtnOutlineGreen>
            )}
          </Col>
        </Row>

        <br />

        <Row className="search-add-cont m-t-20" style={{ rowGap: '1rem' }}>
          <Col sm="6" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.admin }}
              onFilter={({ search }) => onFilter({ admin: search })}
              label={t('ADMIN_SEARCH')}
            />
          </Col>

          <Col sm="6" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.signalman }}
              onFilter={({ search }) => onFilter({ signalman: search })}
              label={t('SIGNALMAN_SEARCH')}
            />
          </Col>

          <Col sm="6" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.condo }}
              onFilter={({ search }) => onFilter({ condo: search })}
              label={t('CONDO_SEARCH')}
            />
          </Col>

          <Col sm="6" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.notes }}
              onFilter={({ search }) => onFilter({ notes: search })}
              label={t('NOTES_SEARCH')}
            />
          </Col>

          <Col sm="12" md="6" lg="3" className="select-wrapper">
            <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
              <Row>
                <Col xs="6">
                  <Label>{t('ACCOUNTABILITY_MONTH_FILTER')}</Label>
                </Col>
                <Col xs="6">
                  <FormGroup>
                    <CustomInput
                      type="select"
                      id="accountabilityMonth"
                      name="accountabilityMonth"
                      value={query && query.accountabilityMonth}
                      onChange={({ target: { value: accountabilityMonth } }) =>
                        onFilter({ accountabilityMonth })
                      }
                    >
                      {generateMonthOptions()}
                    </CustomInput>
                  </FormGroup>
                </Col>
              </Row>
            </ContFilterYear>
          </Col>
        </Row>

        <br />

        <SignalsCondoRoyaltyTable
          data={dataRevSignals || []}
          total={total}
          query={query}
          onFilter={onFilter}
          showEmptyTableMessage={true}
          profile={profile}
          bottomRow={
            <TotalSignalsCondoRevenuesRow
              totals={getVariableRevenuesSignalsTotals(dataRevSignals)}
              route="variable-revenues-signals"
              activeRoute={false}
            />
          }
        />

        <ContPaginationPage>
          <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

          <FormGroup>
            <CustomInput
              type="select"
              className="pagination-select"
              id="perPage"
              name="perPage"
              value={query.limit}
              onChange={({ target: { value: limit } }) => {
                onFilter({ limit });
              }}
            >
              {generatePaginationOptions()}
            </CustomInput>
          </FormGroup>
        </ContPaginationPage>
      </section>

      <ReportingModal
        toggle={toggle}
        isOpen={isOpen}
        onSubmit={onSubmit}
        amount={amount}
        query={query}
      />
    </>
  );
};

export default SignalsCondo;
